import { useState, useEffect, useCallback, useRef } from "react";
import { IPart } from "../../models/Part";
import useEbaySyncMutations, { ISyncEbayListingWithPartId } from "../../data/useEbaySyncMutations";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import Card from "../shared/Card/Card";
import useEbay from "../../data/useEbay";

interface SyncEbayListingsModalProps {
  parts: IPart[];
  onClose: () => void;
}

export default function SyncEbayListingsModal({ parts, onClose }: SyncEbayListingsModalProps) {
  const { syncEbayListings, applySyncEbayListing, applySyncAllEbayListings } = useEbaySyncMutations();
  const { addNotification } = useNotification();
  const { policies } = useEbay();
  const [syncResults, setSyncResults] = useState<ISyncEbayListingWithPartId[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [applyingPartId, setApplyingPartId] = useState<string | null>(null);
  const hasSyncedRef = useRef(false);

  const getPolicyName = (policyId: string | undefined, policyType: 'fulfillment' | 'payment' | 'return'): string => {
    if (!policyId || !policies.data?.data) return "N/A";

    const policyList =
      policyType === 'fulfillment' ? policies.data.data.fulfilmentPolicies :
        policyType === 'payment' ? policies.data.data.paymentPolicies :
          policies.data.data.returnPolicies;

    const policy = policyList.find(p => p.id === policyId);
    return policy?.name || policyId;
  };

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const partIds = parts.map(p => p.id);
      const result = await syncEbayListings.mutateAsync(partIds);
      if (result.data) {
        setSyncResults(result.data);
      } else {
        addNotification({ primaryText: "Failed to sync eBay listings", variant: "error" });
      }
    } catch (error: any) {
      addNotification({ primaryText: error?.response?.data?.message || "Failed to sync eBay listings", variant: "error" });
    } finally {
      setIsSyncing(false);
    }
  }, [parts, syncEbayListings, addNotification]);

  useEffect(() => {
    if (parts.length > 0 && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      handleSync();
    }
  }, [parts.length, handleSync]);

  const handleApplySync = async (partId: string, syncData: ISyncEbayListingWithPartId["syncData"]) => {
    if (!syncData) {
      addNotification({ primaryText: "No sync data available for this part", variant: "error" });
      return;
    }

    setApplyingPartId(partId);
    try {
      await applySyncEbayListing.mutateAsync({ partId, syncData });
      addNotification({ primaryText: "Successfully synced part data", variant: "success" });

      // Refresh sync results to show updated state
      handleSync();
    } catch (error: any) {
      addNotification({ primaryText: error?.response?.data?.message || "Failed to apply sync", variant: "error" });
    } finally {
      setApplyingPartId(null);
    }
  };

  const handleSyncAll = async () => {
    const partIds = parts.map(p => p.id);
    try {
      await applySyncAllEbayListings.mutateAsync(partIds);
      addNotification({ primaryText: "Successfully synced all parts", variant: "success" });
      onClose();
    } catch (error: any) {
      addNotification({ primaryText: error?.response?.data?.message || "Failed to sync all parts", variant: "error" });
    }
  };

  const getPartName = (partId: string) => {
    return parts.find(p => p.id === partId)?.tagNumber || partId;
  };

  if (isSyncing) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <LoadingWheel />
          <span className="ml-4">Syncing eBay listings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold">Sync eBay Listings</h2>
          <h3 className="">Choose which parts you want to replace the current Wilma data for the current eBay data.</h3>
          <h3 className="">This action is irreversible.</h3>
        </div>
        <div className="flex gap-2">
          <ButtonInput
            label="Replace All"
            onClick={handleSyncAll}
            isSubmit={false}
            classes=""
            disabled={applySyncAllEbayListings.isLoading || syncResults.length === 0}
          />
          <ButtonInput
            label="Close"
            onClick={onClose}
            isSubmit={false}
            classes="bg-gray-500 hover:bg-gray-600"
          />
        </div>
      </div>

      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {syncResults.map((result) => {
          const part = parts.find(p => p.id === result.partId);
          const hasSyncData = result.syncData !== null;
          const isApplying = applyingPartId === result.partId;

          return (
            <Card key={result.partId} bodyClassName="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg">
                    {part?.tagNumber || result.partId} eBay Data:
                  </h3>
                  {part?.offers?.[0]?.title && (
                    <p className="text-sm text-gray-600 mt-1">Current: {part.offers[0].title}</p>
                  )}
                </div>
                <ButtonInput
                  label={isApplying ? "Syncing..." : "Replace Wilma Data With eBay Data"}
                  onClick={() => handleApplySync(result.partId, result.syncData)}
                  isSubmit={false}
                  classes=""
                  disabled={!hasSyncData || isApplying || applySyncEbayListing.isLoading}
                />
              </div>

              {hasSyncData && result.syncData ? (
                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium">Title:</span>
                      <p className="text-gray-700">{result.syncData.title || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium">API Source:</span>
                      <p className="text-gray-700">{result.syncData.apiSource || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium">Price:</span>
                      <p className="text-gray-700">
                        {result.syncData.buyItNowPrice
                          ? `£${result.syncData.buyItNowPrice.toFixed(2)}`
                          : result.syncData.auctionStartPrice
                            ? `£${result.syncData.auctionStartPrice.toFixed(2)} (Auction)`
                            : "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium">Category:</span>
                      <p className="text-gray-700">{result.syncData.categoryId || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium">Condition:</span>
                      <p className="text-gray-700">{result.syncData.condition || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium">Best Offer:</span>
                      <p className="text-gray-700">{result.syncData.enableBestOffer ? "Yes" : "No"}</p>
                    </div>
                    <div>
                      <span className="font-medium">Fulfillment Policy:</span>
                      <p className="text-gray-700">{getPolicyName(result.syncData.fulfillmentPolicyId, 'fulfillment')}</p>
                    </div>
                    <div>
                      <span className="font-medium">Payment Policy:</span>
                      <p className="text-gray-700">{getPolicyName(result.syncData.paymentPolicyId, 'payment')}</p>
                    </div>
                    <div>
                      <span className="font-medium">Return Policy:</span>
                      <p className="text-gray-700">{getPolicyName(result.syncData.returnPolicyId, 'return')}</p>
                    </div>
                  </div>

                  {result.syncData.description && (
                    <div className="mt-2">
                      <Card
                        title="Description"
                        collapsible={true}
                        defaultOpen={false}
                        bodyClassName="p-4"
                      >
                        <div
                          className="text-gray-700 text-sm"
                          dangerouslySetInnerHTML={{ __html: result.syncData.description }}
                        />
                      </Card>
                    </div>
                  )}

                  {result.syncData.aspects && Object.keys(result.syncData.aspects).length > 0 && (
                    <div className="mt-2">
                      <span className="font-medium">Aspects:</span>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {Object.entries(result.syncData.aspects).slice(0, 5).map(([key, values]) => (
                          <span key={key} className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {key}: {Array.isArray(values) ? values.join(", ") : values}
                          </span>
                        ))}
                        {Object.keys(result.syncData.aspects).length > 5 && (
                          <span className="text-xs text-gray-500">
                            +{Object.keys(result.syncData.aspects).length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-red-600">
                  Failed to sync: No eBay listing found for this part
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

